import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { connect } from 'react-redux';
import debounce from 'lodash.debounce';

// components
import { FormattedMessage } from 'react-intl';
import ArrowDownIcon from 'dibs-vg/dist/react/arrow-down';

// styles
import styles from './SbRespStickyMenu.scss';

const RESET_PADDING = 115;
const GLOBAL_NAV_HEIGHT = 99;

export class SbRespStickyMenuComponent extends React.Component {
    constructor() {
        super();
        this.state = {
            isIE11: false,
            showCta: false,
            removeCta: false,
        };
        this.menuTicking = false;
        this.stickyMenu = React.createRef();
        this.stickyMenuWrapper = React.createRef();
        this.stickyMenuContents = React.createRef();
        this.scrollToTop = this.scrollToTop.bind(this);
        this.handleMenuScroll = this.handleMenuScroll.bind(this);
        this.handlePageScroll = this.handlePageScroll.bind(this);
        this.resetMenuScroll = debounce(this.resetMenuScroll.bind(this), 200);
    }

    componentDidMount() {
        // determine if browser is IE 11
        // https://stackoverflow.com/questions/21825157/internet-explorer-11-detection
        const isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
        if (isIE11) {
            this.setState({ isIE11 });
        } else {
            const observer = new IntersectionObserver(
                entries => {
                    this.setState({ showCta: !(entries[0].intersectionRatio === 1) });
                },
                {
                    root: this.stickyMenu.current,
                    threshold: 1,
                }
            );
            observer.observe(this.stickyMenuContents.current);

            window.addEventListener('scroll', this.handlePageScroll, { passive: true });
            this.stickyMenu.current.addEventListener('scroll', this.handleMenuScroll, {
                passive: true,
            });
        }
    }

    componentDidUpdate(prevProps) {
        const { refetchInFlight: prevRefetchInFlight } = prevProps;
        const { refetchInFlight } = this.props;
        const { isIE11 } = this.state;

        if (prevRefetchInFlight && !refetchInFlight && !isIE11) {
            this.scrollToTop();
        }
    }

    getRect(reactElem) {
        return reactElem.current.getBoundingClientRect();
    }

    scrollToTop() {
        const { top } = this.getRect(this.stickyMenuWrapper);

        // only jump to top if menu is in sticky mode
        if (top - GLOBAL_NAV_HEIGHT <= 0) {
            window.scrollTo(window.scrollX, window.scrollY + top - RESET_PADDING);
        }
    }

    resetMenuScroll() {
        // bug with scrollTo in Edge, use scrollTop instead
        // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/15534521/
        if (this.stickyMenu.current.scrollTop) {
            this.stickyMenu.current.scrollTop = 0;
        }
    }

    handlePageScroll() {
        if (window.scrollY === 0) {
            this.resetMenuScroll();
        }
    }

    handleMenuScroll() {
        if (!this.menuTicking && !this.state.removeCta) {
            this.setState({ removeCta: true }, () => {
                // cta is gone we no longer need this
                this.stickyMenu.current.removeEventListener('scroll', this.handleMenuScroll);
                this.menuTicking = false;
            });
        }
        this.menuTicking = true;
    }

    render() {
        const { children } = this.props;
        const { showCta, removeCta, isIE11 } = this.state;
        const ctaClassNames = classnames(styles.ctaContent, styles.fadeIn, {
            [styles.fadeOut]: removeCta,
        });
        const stickyMenuClassNames = classnames(styles.menu, {
            [styles.fullHeight]: isIE11,
        });

        return (
            <div ref={this.stickyMenuWrapper} className={styles.wrapper}>
                <div ref={this.stickyMenu} className={stickyMenuClassNames}>
                    <div ref={this.stickyMenuContents}>{children}</div>
                </div>
                {showCta && !isIE11 && (
                    <div className={styles.ctaWrapper}>
                        <div className={ctaClassNames}>
                            <div className={styles.arrow}>
                                <ArrowDownIcon />
                            </div>
                            <span className={styles.ctaText}>
                                <FormattedMessage
                                    id="SbRespStickyMenu.ctaText"
                                    defaultMessage="Scroll to see more filters"
                                />
                            </span>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

SbRespStickyMenuComponent.propTypes = {
    children: PropTypes.oneOfType([PropTypes.array, PropTypes.object]).isRequired,
    refetchInFlight: PropTypes.bool,
};

const mapStateToProps = state => {
    const { filters } = state;

    return {
        refetchInFlight: filters.refetchInFlight,
    };
};

export const SbRespStickyMenu = connect(mapStateToProps)(SbRespStickyMenuComponent);
